import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core'
import { ApiService } from 'src/app/services/api.service'
import { NetworkSandboxService, SyncPhaseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { LaunchRestoreSettings, LaunchSyncDialogBase } from 'src/app/account/components/launch-sync-dialog-base'
import { FormBuilder } from '@angular/forms'
import { ValidationOutput } from 'src/app/shared/string-list-control/string-list-control.component'
import { validCidr, validDomain } from '../../../shared/utils'
import { Subscription } from 'rxjs'
import { subscriptionActive } from '../../../../utils/rxjs-utils'
import { finalize, first } from 'rxjs/operators'
import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

interface Suggestion {
  value: string
  reason: string
}

const DEFAULT_AWS_SUGGESTED_DOMAINS: Suggestion[] = [{ value: '.amazonaws.com', reason: 'All AWS services' }]
const DEFAULT_AZURE_SUGGESTED_DOMAINS: Suggestion[] = [
  { value: 'management.azure.com', reason: 'Azure resource management APIs' },
  { value: '.vault.azure.net', reason: 'All Azure key vaults' },
  { value: '.blob.core.windows.net', reason: 'All Azure storage account blobs' },
]

@Component({
  selector: 'application-test-config-dialog',
  templateUrl: './application-test-config-dialog.component.html',
  styleUrls: ['./application-test-config-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ApplicationTestConfigDialogComponent extends LaunchSyncDialogBase {
  @Input() currentlySyncing: boolean = false
  @Output() launch = new EventEmitter<Array<LaunchRestoreSettings>>()

  public whitelistPlaceholderText = '8.8.8.0/24, 34.0.0.8...'
  public domainAllowlistPlaceholderText = 'registry.hub.docker.com, quay.io'
  private _whitelistIsValid: boolean = true
  private _domainAllowlistIsValid: boolean = true

  private recentlyBlockedIpsSubscription?: Subscription = null
  private recentlyBlockedDomainsSubscription?: Subscription = null

  public suggestedDomains = DEFAULT_AWS_SUGGESTED_DOMAINS
  public suggestedCidrs: Suggestion[] = []

  constructor(
    private formBuilder: FormBuilder,
    applicationProviderService: ApplicationProviderService,
    accountService: AccountService,
    apiService: ApiService,
    uiService: UiService,
    accessLevelService: AccessLevelService
  ) {
    super(applicationProviderService, accountService, apiService, uiService, accessLevelService)
  }

  launchTestSettingsFormModel = this.formBuilder.group({
    // Use Array<string> here instead of [] to convey type :)
    sandboxWhitelist: [new Array<string>()],
    sandboxAllowedDomains: [new Array<string>()],
    allowS3Checked: [false],
    allowDynamoChecked: [false],
  })

  ngOnInit() {
    super.ngOnInit()
    this.launchTestSettingsFormModel.setValue({
      sandboxWhitelist: this.application.networkSandboxWhitelist,
      sandboxAllowedDomains: this.application.networkSandboxAllowedDomains,
      allowS3Checked: this.application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_S3),
      allowDynamoChecked: this.application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_DYNAMODB),
    })

    // Also apply to other apps in the same region pair that are also in failover test
    for (let app of this._applications) {
      if (app.syncPhase == SyncPhaseDto.FAILOVER_TEST) {
        this.selectedApplicationIds.add(app.applicationId)
      }
    }

    const allowedDomains = new Set(this.application.networkSandboxAllowedDomains || [])
    const allowedCidrs = new Set(this.application.networkSandboxWhitelist || [])

    const defaultDomains = this.isAzureApplication ? DEFAULT_AZURE_SUGGESTED_DOMAINS : DEFAULT_AWS_SUGGESTED_DOMAINS
    this.suggestedDomains = defaultDomains.filter((sd) => !allowedDomains.has(sd.value))

    if (this.application.advancedNetworkSandboxEnabled) {
      const endTime = new Date()
      const startTime = new Date()
      startTime.setHours(startTime.getHours() - 24)

      this.recentlyBlockedIpsSubscription = this.apiService
        .getNetworkSandboxBlockedIps(this.accountService.accountId, this.application.applicationId, startTime, endTime)
        .pipe(
          first(),
          finalize(() => {
            this.recentlyBlockedIpsSubscription = null
            this.cdr.markForCheck()
          })
        )
        .subscribe((response) => {
          this.suggestedCidrs = response.results
            .map((r) => ({ value: r.endpoint + '/32', reason: 'recently blocked' }))
            .filter((s) => !allowedCidrs.has(s.value))
            .sort((a, b) => a.value.localeCompare(b.value))
        })

      this.recentlyBlockedDomainsSubscription = this.apiService
        .getNetworkSandboxBlockedDomains(
          this.accountService.accountId,
          this.application.applicationId,
          startTime,
          endTime
        )
        .pipe(
          first(),
          finalize(() => {
            this.recentlyBlockedDomainsSubscription = null
            this.cdr.markForCheck()
          })
        )
        .subscribe((response) => {
          this.suggestedDomains = response.results
            .map((r) => ({ value: r.endpoint, reason: 'recently blocked' }))
            .concat(defaultDomains)
            .filter((s) => !allowedDomains.has(s.value))
            .sort((a, b) => a.value.localeCompare(b.value))
        })
    }
  }

  protected applicationIsCandidate(application: Application) {
    return application.syncPhase == SyncPhaseDto.FAILOVER_TEST
  }

  get isAzureApplication(): boolean {
    return this.application?.type === 'azure'
  }

  onUpdateClick(): void {
    this.launch.emit(
      this.selectedApplications.reduce((accum, app) => {
        accum.push({
          application: app,
          networkSandboxEnabled: true,
          networkSandboxWhitelist: this.networkSandboxWhitelist,
          networkSandboxAllowedDomains: this.networkSandboxAllowedDomains,
          networkSandboxAllowedServices: this.networkSandboxAllowedServices,
          advancedNetworkSandbox: this.advancedNetworkSandbox,
        } as LaunchRestoreSettings)
        return accum
      }, [] as Array<LaunchRestoreSettings>)
    )
  }

  get networkSandboxWhitelist(): string[] {
    return this.launchTestSettingsFormModel.get('sandboxWhitelist').value
  }

  get networkSandboxAllowedDomains(): string[] {
    return this.launchTestSettingsFormModel.get('sandboxAllowedDomains').value
  }

  get networkSandboxAllowedServices(): string[] {
    let allowedServices = []

    if (this.launchTestSettingsFormModel.get('allowS3Checked').value) {
      allowedServices.push(NetworkSandboxService.AWS_S3)
    }

    if (this.launchTestSettingsFormModel.get('allowDynamoChecked').value) {
      allowedServices.push(NetworkSandboxService.AWS_DYNAMODB)
    }

    return allowedServices
  }

  public isValidCidr(candidate: string): boolean {
    return validCidr(candidate)
  }

  public isValidDomain(domain: string): boolean {
    return validDomain(domain)
  }

  public normalizeCidr(candidate: string): string {
    return candidate.includes('/') ? candidate : candidate + '/32'
  }

  whitelistValidated(event: ValidationOutput): void {
    this._whitelistIsValid = !event.invalid
  }

  sandboxDomainsValidated(event: ValidationOutput): void {
    this._domainAllowlistIsValid = !event.invalid
  }

  get advancedNetworkSandbox() {
    return this.application.advancedNetworkSandboxEnabled
  }

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  get canUpdate(): boolean {
    return !this.launching && !this.currentlySyncing && this._whitelistIsValid && this._domainAllowlistIsValid
  }

  get currentlySyncingMessage() {
    return this.currentlySyncing && this.application?.syncPhase !== SyncPhaseDto.STANDBY
  }

  get otherApplicationNames() {
    return this.selectedApplications
      .filter((app) => app.applicationId != this.application.applicationId)
      .map((app) => app.name)
  }

  get loadingSuggestedCidrs(): boolean {
    return subscriptionActive(this.recentlyBlockedIpsSubscription)
  }

  get loadingSuggestedDomains(): boolean {
    return subscriptionActive(this.recentlyBlockedDomainsSubscription)
  }

  addAllowedDomain(domain: string) {
    const control = this.launchTestSettingsFormModel.get('sandboxAllowedDomains')
    let value = control.value || []
    value.push(domain)
    control.setValue(value)
    this.suggestedDomains = this.suggestedDomains.filter((s) => s.value != domain)
  }

  addAllowedCidr(cidr: string) {
    const control = this.launchTestSettingsFormModel.get('sandboxWhitelist')
    let value = control.value || []
    value.push(cidr)
    control.setValue(value)
    this.suggestedCidrs = this.suggestedCidrs.filter((s) => s.value != cidr)
  }

  protected getRequiredPermission(): AppPermission {
    return AppPermission.MANAGE_TEST
  }

  // This dialog edits sandbox network settings on an in-flight test failover —
  // it doesn't pick a new recovery point, so the per-app RP refresh fan-out
  // would only delay UPDATE without informing any decision the form makes.
  protected override get refreshRecoveryPointsOnOpen(): boolean {
    return false
  }
}
